import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack,
  Autocomplete
} from '@mui/material';
import { 
  Search as SearchIcon, 
  FileDownload as FileDownloadIcon,
  Refresh as RefreshIcon,
  Speed as SpeedIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { inventoryApi } from '../api/inventoryApi';
import { itemApi } from '../../settings/items/api/itemApi';
import { Item } from '../../settings/items/types/item.types';
import { itemLocationApi } from '../../settings/items/api/itemLocationApi';
import { hospitalApi } from '../../../api/hospitalApi';
import { format, subMonths } from 'date-fns';

// FSN Classification criteria
const FSN_CRITERIA = {
  FAST_MOVING: 'Items with high consumption rate (top 20% of items)',
  SLOW_MOVING: 'Items with moderate consumption rate (middle 30% of items)',
  NON_MOVING: 'Items with low or no consumption (bottom 50% of items)'
};

// Define time periods for analysis
const TIME_PERIODS = [
  { value: 3, label: 'Last 3 Months' },
  { value: 6, label: 'Last 6 Months' },
  { value: 12, label: 'Last 12 Months' }
];

// Interface for item consumption data
interface ItemConsumption {
  id: string;
  itemId: string;
  itemCode: string;
  itemName: string;
  itemGroup: string;
  unit: string;
  totalConsumption: number;
  consumptionByMonth: {
    month: string;
    quantity: number;
  }[];
  averageMonthlyConsumption: number;
  currentStock: number;
  classification: 'F' | 'S' | 'N';
  reorderLevel?: number;
  lastTransaction?: string;
}

const FSNAnalysisReport: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [itemGroupFilter, setItemGroupFilter] = useState<string>('All Groups');
  const [itemFilter, setItemFilter] = useState<string>('all');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [items, setItems] = useState<Item[]>([]);
  const [itemConsumption, setItemConsumption] = useState<ItemConsumption[]>([]);
  const [itemGroups, setItemGroups] = useState<string[]>(['All Groups']);
  const [hospitalInfo, setHospitalInfo] = useState<any>(null);
  const [timePeriod, setTimePeriod] = useState<number>(3); // Default to 3 months
  const [fsnThresholds] = useState<{fast: number, slow: number}>({
    fast: 80, // Top 20%
    slow: 50  // Next 30% (50-80%)
  });
  // No need for a report ref since we're not generating PDF
  const reportRef = useRef<HTMLDivElement>(null);

  // Load data on component mount
  useEffect(() => {
    loadItems();
    loadHospitalInfo();
  }, []);

  // Load data when time period changes
  useEffect(() => {
    if (items.length > 0) {
      loadConsumptionData();
    }
  }, [timePeriod, items]);

  // Load hospital info for reports
  const loadHospitalInfo = async () => {
    try {
      const info = await hospitalApi.getCompanyProfile();
      setHospitalInfo(info);
    } catch (error) {
      console.error('Error loading hospital info:', error);
    }
  };

  // Load items
  const loadItems = async () => {
    try {
      setLoading(true);
      const itemsData = await itemApi.getAllItems();
      setItems(itemsData);
      
      // Extract unique item groups
      const groups = ['All Groups', ...new Set(itemsData.map(item => item.itemGroup))];
      setItemGroups(groups);
      
      // Load consumption data after items are loaded
      await loadConsumptionData();
    } catch (error) {
      console.error('Error loading items:', error);
      enqueueSnackbar('Failed to load items', { variant: 'error' });
      setLoading(false);
    }
  };

  // Load consumption data for FSN analysis
  const loadConsumptionData = async () => {
    try {
      setLoading(true);
      
      // Calculate date range for analysis
      const endDate = new Date();
      const startDate = subMonths(endDate, timePeriod);
      
      // Format dates for API
      const startDateStr = format(startDate, 'yyyy-MM-dd');
      const endDateStr = format(endDate, 'yyyy-MM-dd');
      
      // Get transactions for the period
      const transactions = await inventoryApi.getInventoryTransactionsByDateRange(
        startDateStr,
        endDateStr,
        true // Include details
      );
      
      // Get current stock levels (fetch all rows, not just the default page of 10)
      const itemLocations = await itemLocationApi.getItemLocations({
        page: 1,
        limit: 100000,
        includeItem: true,
        includeLocation: true
      });
      
      // Process data to calculate consumption
      processConsumptionData(transactions, itemLocations);
    } catch (error) {
      console.error('Error loading consumption data:', error);
      enqueueSnackbar('Failed to load consumption data', { variant: 'error' });
      setLoading(false);
    }
  };

  // Process transaction data to calculate consumption
  const processConsumptionData = (transactions: any[], itemLocations: any[]) => {
    // Group transactions by item
    const itemTransactions: Record<string, any[]> = {};
    
    // Initialize with all items
    items.forEach(item => {
      itemTransactions[item.id] = [];
    });
    
    // Add transactions to respective items
    transactions.forEach(transaction => {
      if (!transaction.details) return;
      
      transaction.details.forEach((detail: any) => {
        if (!detail.item) return;
        
        // Only count outgoing transactions (consumption)
        if (detail.mode === 'DEBIT') {
          if (!itemTransactions[detail.itemId]) {
            itemTransactions[detail.itemId] = [];
          }
          
          itemTransactions[detail.itemId].push({
            date: transaction.transactionDate,
            quantity: Math.abs(detail.quantity),
            type: transaction.type
          });
        }
      });
    });
    
    // Calculate consumption by month for each item
    const consumptionData: ItemConsumption[] = [];
    
    items.forEach(item => {
      const transactions = itemTransactions[item.id] || [];
      
      // Group transactions by month
      const monthlyConsumption: Record<string, number> = {};
      
      // Initialize all months in the period with zero consumption
      for (let i = 0; i < timePeriod; i++) {
        const monthDate = subMonths(new Date(), i);
        const monthKey = format(monthDate, 'yyyy-MM');
        monthlyConsumption[monthKey] = 0;
      }
      
      // Add transaction quantities to respective months
      transactions.forEach(transaction => {
        const monthKey = format(new Date(transaction.date), 'yyyy-MM');
        if (monthlyConsumption[monthKey] !== undefined) {
          monthlyConsumption[monthKey] += transaction.quantity;
        }
      });
      
      // Convert to array format
      const consumptionByMonth = Object.entries(monthlyConsumption).map(([month, quantity]) => ({
        month,
        quantity
      })).sort((a, b) => a.month.localeCompare(b.month));
      
      // Calculate total and average consumption
      const totalConsumption = consumptionByMonth.reduce((sum, month) => sum + month.quantity, 0);
      const averageMonthlyConsumption = totalConsumption / timePeriod;
      
      // Get current stock level
      const itemStockLocations = itemLocations.filter(loc => loc.itemId === item.id);
      const currentStock = itemStockLocations.reduce((sum, loc) => sum + loc.balance, 0);
      
      // Get last transaction date
      const lastTransaction = transactions.length > 0 
        ? transactions.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())[0].date
        : undefined;
      
      // Add to consumption data (classification will be set later)
      consumptionData.push({
        id: item.id,
        itemId: item.id,
        itemCode: item.itemCode,
        itemName: item.itemName,
        itemGroup: item.itemGroup,
        unit: item.unitOfMeasure,
        totalConsumption,
        consumptionByMonth,
        averageMonthlyConsumption,
        currentStock,
        classification: 'N', // Default, will be updated
        reorderLevel: item.reorderLevel || undefined,
        lastTransaction
      });
    });
    
    // Sort by total consumption (descending)
    consumptionData.sort((a, b) => b.totalConsumption - a.totalConsumption);
    
    // Apply FSN classification based on Pareto principle
    const totalItems = consumptionData.length;
    const fastMovingThreshold = Math.floor(totalItems * (fsnThresholds.fast / 100));
    const slowMovingThreshold = Math.floor(totalItems * (fsnThresholds.slow / 100));
    
    consumptionData.forEach((item, index) => {
      if (index < fastMovingThreshold) {
        item.classification = 'F'; // Fast moving
      } else if (index < slowMovingThreshold) {
        item.classification = 'S'; // Slow moving
      } else {
        item.classification = 'N'; // Non-moving
      }
    });
    
    setItemConsumption(consumptionData);
    setLoading(false);
  };

  // Filter items based on selected filters
  const filteredItems = itemConsumption.filter(item => {
    // Filter by item group
    const matchesItemGroup = itemGroupFilter === 'All Groups' || 
      item.itemGroup === itemGroupFilter;
    
    // Filter by specific item
    const matchesItem = itemFilter === 'all' || item.id === itemFilter;
    
    // Filter by search term
    const matchesSearch = searchTerm === '' || 
      item.itemName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.itemCode.toLowerCase().includes(searchTerm.toLowerCase());
    
    return matchesItemGroup && matchesItem && matchesSearch;
  })
  // Show items with current stock first; 0-stock items fall to the bottom.
  // Stable sort preserves the consumption-desc order within the same stock level.
  .sort((a, b) => b.currentStock - a.currentStock);

  // Calculate summary statistics
  const summary = {
    totalItems: filteredItems.length,
    fastMoving: filteredItems.filter(item => item.classification === 'F').length,
    slowMoving: filteredItems.filter(item => item.classification === 'S').length,
    nonMoving: filteredItems.filter(item => item.classification === 'N').length,
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleItemGroupFilterChange = (event: SelectChangeEvent) => {
    setItemGroupFilter(event.target.value);
    setPage(0);
  };
  
  const handleTimePeriodChange = (event: SelectChangeEvent) => {
    setTimePeriod(Number(event.target.value));
    setPage(0);
  };
  
  const handleRefresh = () => {
    setLoading(true);
    loadConsumptionData();
  };

  const getClassificationColor = (classification: string) => {
    switch (classification) {
      case 'F': return 'success';
      case 'S': return 'warning';
      case 'N': return 'error';
      default: return 'default';
    }
  };

  const getClassificationLabel = (classification: string) => {
    switch (classification) {
      case 'F': return 'Fast Moving';
      case 'S': return 'Slow Moving';
      case 'N': return 'Non-Moving';
      default: return 'Unknown';
    }
  };

  const getClassificationIcon = (classification: string): React.ReactElement | undefined => {
    switch (classification) {
      case 'F': return <SpeedIcon fontSize="small" />;
      case 'S': return <TrendingFlatIcon fontSize="small" />;
      case 'N': return <TrendingDownIcon fontSize="small" />;
      default: return undefined;
    }
  };
  
  // Export to CSV
  const handleExportCSV = () => {
    // Create headers
    const headers = [
      'Item Code',
      'Item Name',
      'Item Group',
      'Unit',
      'Current Stock',
      'Total Consumption',
      'Avg. Monthly Consumption',
      'Classification',
      'Reorder Level',
      'Last Transaction'
    ];
    
    // Create rows
    const rows = filteredItems.map(item => {
      const row = [
        item.itemCode,
        item.itemName,
        item.itemGroup,
        item.unit,
        item.currentStock.toString(),
        item.totalConsumption.toString(),
        item.averageMonthlyConsumption.toFixed(2),
        getClassificationLabel(item.classification),
        item.reorderLevel?.toString() || '-',
        item.lastTransaction ? new Date(item.lastTransaction).toLocaleDateString() : 'N/A'
      ];
      
      return row;
    });
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(r => r.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))
    ].join('\r\n');
    
    // Download CSV file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date();
    const dateStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    link.setAttribute('download', `FSN_Analysis_${dateStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        FSN Analysis Report
      </Typography>
      <Typography variant="subtitle1" color="text.secondary" gutterBottom>
        Fast, Slow, and Non-moving items based on consumption patterns
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Search by Item Name/Code"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <IconButton>
                      <SearchIcon />
                    </IconButton>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Autocomplete
                size="small"
                options={items}
                getOptionLabel={(option) => `${option.itemName} (${option.itemCode})`}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                value={items.find((i) => i.id === itemFilter) || null}
                onChange={(_e, newValue) => {
                  setItemFilter(newValue ? newValue.id : 'all');
                  setPage(0);
                }}
                renderInput={(params) => <TextField {...params} label="Item" />}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Item Group</InputLabel>
                <Select
                  value={itemGroupFilter}
                  label="Item Group"
                  onChange={handleItemGroupFilterChange}
                >
                  {itemGroups.map((group) => (
                    <MenuItem key={group} value={group}>
                      {group}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Time Period</InputLabel>
                <Select
                  value={timePeriod.toString()}
                  label="Time Period"
                  onChange={handleTimePeriodChange}
                >
                  {TIME_PERIODS.map((period) => (
                    <MenuItem key={period.value} value={period.value.toString()}>
                      {period.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Stack direction="row" spacing={1} sx={{ width: '100%' }}>
                <Button 
                  variant="outlined" 
                  color="primary"
                  fullWidth
                  startIcon={<FileDownloadIcon />}
                  disabled={loading || filteredItems.length === 0}
                  onClick={handleExportCSV}
                >
                  CSV
                </Button>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleRefresh}
                  disabled={loading}
                >
                  <RefreshIcon />
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Fast Moving Items
              </Typography>
              <Typography variant="h3" color="success.main">
                {summary.fastMoving}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round((summary.fastMoving / summary.totalItems) * 100)}% of inventory
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Slow Moving Items
              </Typography>
              <Typography variant="h3" color="warning.main">
                {summary.slowMoving}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round((summary.slowMoving / summary.totalItems) * 100)}% of inventory
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Non-Moving Items
              </Typography>
              <Typography variant="h3" color="error.main">
                {summary.nonMoving}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {Math.round((summary.nonMoving / summary.totalItems) * 100)}% of inventory
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Total Items
              </Typography>
              <Typography variant="h3">
                {summary.totalItems}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Analysis period: {TIME_PERIODS.find(p => p.value === timePeriod)?.label}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : filteredItems.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="subtitle1" color="text.secondary">
              No items found. Try adjusting your filters.
            </Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Item Code</TableCell>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Item Group</TableCell>
                  <TableCell>Current Stock</TableCell>
                  <TableCell>Total Consumption</TableCell>
                  <TableCell>Avg. Monthly</TableCell>
                  <TableCell>Classification</TableCell>
                  <TableCell>Reorder Level</TableCell>
                  <TableCell>Last Transaction</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredItems
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.itemCode}</TableCell>
                      <TableCell>{item.itemName}</TableCell>
                      <TableCell>{item.itemGroup}</TableCell>
                      <TableCell>{item.currentStock}</TableCell>
                      <TableCell>{item.totalConsumption}</TableCell>
                      <TableCell>{item.averageMonthlyConsumption.toFixed(2)}</TableCell>
                      <TableCell>
                        <Chip 
                          icon={getClassificationIcon(item.classification)}
                          label={getClassificationLabel(item.classification)} 
                          color={getClassificationColor(item.classification) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {item.reorderLevel || '-'}
                      </TableCell>
                      <TableCell>
                        {item.lastTransaction 
                          ? new Date(item.lastTransaction).toLocaleDateString() 
                          : 'N/A'}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredItems.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>

      {/* FSN Classification Criteria */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            FSN Classification Criteria
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="success.main">
                Fast Moving (F)
              </Typography>
              <Typography variant="body2">
                {FSN_CRITERIA.FAST_MOVING}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="warning.main">
                Slow Moving (S)
              </Typography>
              <Typography variant="body2">
                {FSN_CRITERIA.SLOW_MOVING}
              </Typography>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" color="error.main">
                Non-Moving (N)
              </Typography>
              <Typography variant="body2">
                {FSN_CRITERIA.NON_MOVING}
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
};

export default FSNAnalysisReport;
